import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import { TM } from "./TM";

const NAV_ITEMS = [
  { label: "Principles", href: "#pillars" },
  { label: "Education", href: "#education" },
  { label: "FinTech", href: "#tradingview" },
  { label: "Store", href: LINKS.store, external: true },
];

export const Nav = ({ topOffsetPx = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
      const docH =
        document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Scroll-spy: highlights the nav link for the section currently in view
  useEffect(() => {
    const ids = NAV_ITEMS.filter((i) => !i.external).map((i) =>
      i.href.replace("#", ""),
    );
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      data-testid="vault-nav"
      style={{ top: topOffsetPx }}
      className={`fixed inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 backdrop-blur-xl bg-[#0a0a0a]/70 border-b border-[rgba(201,162,91,0.18)]"
          : "py-5"
      }`}
    >
      {/* Scroll progress bar — top edge */}
      <div
        aria-hidden
        data-testid="scroll-progress-track"
        className="absolute top-0 inset-x-0 h-[2px] bg-[rgba(30,144,255,0.08)] pointer-events-none"
      >
        <div
          data-testid="scroll-progress-bar"
          style={{
            width: `${progress}%`,
            transition: "width 80ms linear",
          }}
          className="h-full bg-gradient-to-r from-[#1e90ff] via-[#6fb3ff] to-[#e7c98a] shadow-[0_0_10px_rgba(30,144,255,0.7)]"
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
        <a
          href="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="brand-seal w-10 h-10 overflow-hidden">
            <img
              src={IMAGES.brandLogo}
              alt="SHYFT Coalition™"
              className="w-9 h-9 object-contain rounded-full"
            />
          </span>
          <span className="font-serif-display text-xl text-white tracking-wider">
            SHYFT<span className="text-gold ml-1.5">Coalition<TM /></span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1 rounded-full border border-[rgba(201,162,91,0.18)] bg-[#14110e]/70 backdrop-blur px-2 py-1.5">
          {NAV_ITEMS.map((item) => {
            const targetId = item.external ? null : item.href.replace("#", "");
            const isActive = targetId && targetId === activeId;
            return (
              <a
                key={item.href}
                href={item.href}
                {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                data-active={isActive ? "true" : undefined}
                className={`relative px-4 py-1.5 text-[13px] tracking-wider uppercase rounded-full transition-all ${
                  isActive
                    ? "text-white"
                    : "text-[#b8b3aa] hover:text-white hover:bg-[rgba(201,162,91,0.08)]"
                }`}
              >
                {item.label}
                {isActive ? (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-[3px] flex items-center gap-1"
                  >
                    <span className="block w-1 h-1 rounded-full bg-[#1e90ff] shadow-[0_0_8px_rgba(30,144,255,0.85)]" />
                    <span className="block w-6 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#1e90ff] to-transparent" />
                    <span className="block w-1 h-1 rounded-full bg-[#1e90ff] shadow-[0_0_8px_rgba(30,144,255,0.85)]" />
                  </span>
                ) : null}
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={LINKS.community}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-join-cta"
            className="btn-gold !py-2 !px-5 text-sm"
          >
            Join Our Community
          </a>
          <a
            href={LINKS.smartlock}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-smartlock-cta"
            className="btn-gold !py-2 !px-5 text-sm"
          >
            SmartLock&trade; Consultation
          </a>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          aria-label="menu"
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[rgba(201,162,91,0.35)] text-gold"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden mx-6 mt-3 rounded-3xl border border-[rgba(201,162,91,0.25)] bg-[#14110e]/95 backdrop-blur-xl p-5 flex flex-col gap-2"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
              data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-4 py-2 rounded-full text-sm tracking-wider uppercase text-[#b8b3aa] hover:text-white hover:bg-[rgba(201,162,91,0.08)]"
            >
              {item.label}
            </a>
          ))}
          <a
            href={LINKS.community}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-join-cta"
            className="btn-gold mt-2"
          >
            Join Our Community
          </a>
          <a
            href={LINKS.smartlock}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-smartlock-cta"
            className="btn-gold"
          >
            SmartLock&trade; Consultation
          </a>
        </div>
      )}
    </header>
  );
};

export default Nav;
